import { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import axios from 'axios';

/**
 * Kaharian Hub Pricing — v3.2 contract.
 *
 * The Hub is the ONLY authoritative price source. This module:
 *   • Fetches /api/pricing/matrix once per session (cached 5 min server-side too).
 *   • Exposes getPrice(action_code) returning a row or undefined.
 *   • Exposes formatCreditRange(row) → "200–310 credits" (never a single number when min < max).
 *
 * RULES (v3.2):
 *   ✗ NEVER hardcode credit values.
 *   ✗ NEVER render end_user_credits_min alone — misleads users.
 *   ✗ NEVER render end_user_credits_max alone for ranged actions.
 *   ✗ NEVER disclose provider names, fallback chains, or tier hints.
 *   ✗ NEVER render ROI, markup, or margin in user-facing UI.
 *   ✓ If getPrice() returns undefined → render "Unavailable" and disable the button.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const HubPricingContext = createContext({
  actions: [],
  isLoading: true,
  error: null,
  getPrice: () => undefined,
  refresh: () => {},
});

export function HubPricingProvider({ children }) {
  const [state, setState] = useState({ actions: [], isLoading: true, error: null });

  const fetchMatrix = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/pricing/matrix`);
      setState({ actions: data.actions || [], isLoading: false, error: null });
    } catch (err) {
      setState({ actions: [], isLoading: false, error: err.message || 'fetch_failed' });
    }
  }, []);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const getPrice = useCallback((actionCode) => {
    if (!actionCode) return undefined;
    return state.actions.find(a => a.action_code === actionCode && a.status === 'active');
  }, [state.actions]);

  const value = useMemo(() => ({
    ...state,
    getPrice,
    refresh: fetchMatrix,
  }), [state, getPrice, fetchMatrix]);

  return <HubPricingContext.Provider value={value}>{children}</HubPricingContext.Provider>;
}

export function useHubPricing() {
  return useContext(HubPricingContext);
}

/**
 * Format a pricing matrix row into a human-readable range.
 *   single value:  "4 credits"
 *   range:         "125–310 credits"
 *   no row:        "—"
 */
export function formatCreditRange(row) {
  if (!row) return '—';
  const unit = row.unit_label || 'credits';
  const min = row.end_user_credits_min ?? row.end_user_credits ?? 0;
  const max = row.end_user_credits_max ?? row.end_user_credits ?? min;
  if (min === max) return `${formatNum(min)} ${unit}`;
  return `${formatNum(min)}–${formatNum(max)} ${unit}`;
}

function formatNum(n) {
  if (typeof n !== 'number' || !isFinite(n)) return '0';
  // Trim trailing zeros: 1.5 → "1.5", 10 → "10", 0.01 → "0.01"
  return Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
}
